import React, { useEffect, useRef, useCallback } from 'react';
import { useInfiniteQuery } from 'react-query';
import { ConversationCard } from './ConversationCard';
import { conversationAPI } from '../../services/api';
import { ConversationSummary, SearchFilters } from '../../types/conversation';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { Card } from '../ui/card';

interface ConversationListProps {
  searchQuery?: string;
  filters?: SearchFilters;
  onSelectConversation?: (conversationId: string) => void;
  onEditConversation?: (conversationId: string, newTitle: string) => void;
  onDeleteConversation?: (conversationId: string) => void;
  selectedConversationId?: string;
}

// Response type for cursor-based pagination (getAllConversations)
interface CursorConversationResponse {
  conversations: ConversationSummary[];
  hasNext: boolean;
  hasPrev: boolean;
  nextCursor?: string;
  prevCursor?: string;
}

// Response type for page-based pagination (searchConversations)
interface PageConversationResponse {
  conversations: ConversationSummary[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
}

type ConversationResponse = CursorConversationResponse | PageConversationResponse;

// Skeleton loader component for individual conversation cards
const ConversationCardSkeleton: React.FC = () => (
  <Card className="p-4 animate-pulse">
    <div className="space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex-1 space-y-2">
          <div className="h-5 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
        <div className="flex space-x-1">
          <div className="h-8 w-8 bg-gray-200 rounded"></div>
          <div className="h-8 w-8 bg-gray-200 rounded"></div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="h-4 bg-gray-200 rounded w-20"></div>
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-5 bg-gray-200 rounded w-16"></div>
        <div className="h-5 bg-gray-200 rounded w-20"></div>
      </div>
    </div>
  </Card>
);

// Loading skeleton for initial load
const ConversationListSkeleton: React.FC = () => (
  <div className="space-y-4">
    {Array.from({ length: 6 }).map((_, index) => (
      <ConversationCardSkeleton key={index} />
    ))}
  </div>
);


export const ConversationList: React.FC<ConversationListProps> = ({
  searchQuery,
  filters,
  onSelectConversation,
  onEditConversation,
  onDeleteConversation,
  selectedConversationId
}) => {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Infinite query for conversations
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<ConversationResponse, Error>(
    ['conversations', searchQuery, filters],
    async ({ pageParam }) => {
      const params: any = {
        limit: 20,
      };

      // Use cursor for pagination if provided
      if (pageParam) {
        params.cursor = pageParam;
        params.direction = 'next';
      }

      // Use search if there's a search query
      if (searchQuery?.trim()) {
        // For search, use page-based pagination
        const searchParams: any = {
          q: searchQuery.trim(),
          page: pageParam || 1,
          limit: 20,
          isActive: true,
        };
        
        // Add filters if provided
        if (filters?.dateRange) {
          searchParams.dateFrom = filters.dateRange.start.toISOString();
          searchParams.dateTo = filters.dateRange.end.toISOString();
        }

        return conversationAPI.searchConversations(searchParams);
      }

      // Add filters for getAllConversations
      if (filters?.dateRange) {
        params.dateFrom = filters.dateRange.start.toISOString();
        params.dateTo = filters.dateRange.end.toISOString();
      }

      // Otherwise use getAllConversations with cursor pagination
      return conversationAPI.getAllConversations(params);
    },
    {
      getNextPageParam: (lastPage) => {
        // Handle cursor-based pagination for getAllConversations
        if ('nextCursor' in lastPage) {
          return (lastPage as CursorConversationResponse).hasNext 
            ? (lastPage as CursorConversationResponse).nextCursor 
            : undefined;
        }
        // Handle page-based pagination for search
        if ('currentPage' in lastPage) {
          const paginated = lastPage as PageConversationResponse;
          return paginated.currentPage < paginated.totalPages 
            ? paginated.currentPage + 1 
            : undefined;
        }
        return undefined;
      },
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Intersection observer for infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
      rootMargin: '100px',
    });

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [handleObserver]);

  // Flatten all pages into a single array
  const conversations = data?.pages.flatMap(page => 
    page.conversations || []
  ) || [];
  const totalCount = conversations.length;

  // Loading state for initial load
  if (isLoading) {
    return <ConversationListSkeleton />;
  }

  // Error state
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {error instanceof Error 
            ? `Failed to load conversations: ${error.message}`
            : 'Failed to load conversations. Please try again.'
          }
        </AlertDescription>
      </Alert>
    );
  }

  // Empty state
  if (conversations.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="h-12 w-12 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {searchQuery?.trim() || filters?.dateRange ? 'No conversations found' : 'No conversations yet'}
        </h3>
        <p className="text-gray-500 max-w-sm mx-auto">
          {searchQuery?.trim() || filters?.dateRange
            ? 'Try adjusting your search or filters to find conversations.'
            : 'Start a conversation by asking a question about your data.'
          }
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        Showing {conversations.length} of {totalCount} conversations
      </div>

      {/* Conversation list */}
      <div className="space-y-4">
        {conversations.map((conversation) => (
          <ConversationCard
            key={conversation.conversationId}
            conversation={conversation}
            onSelect={onSelectConversation}
            onEdit={onEditConversation}
            onDelete={onDeleteConversation}
            isSelected={selectedConversationId === conversation.conversationId}
          />
        ))}
      </div>

      {/* Loading more indicator */}
      {isFetchingNextPage && (
        <div className="flex justify-center py-4">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading more conversations...</span>
          </div>
        </div>
      )}

      {/* Infinite scroll trigger element */}
      <div ref={loadMoreRef} className="h-4" />

      {/* End of list indicator */}
      {!hasNextPage && conversations.length > 0 && (
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground">
            You've reached the end of the list
          </p>
        </div>
      )}
    </div>
  );
};